import { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import DOMPurify from 'dompurify'
import { termsStore } from 'store/termsStore'
import { deviceAtom } from 'store/deviceStore'
import { Header } from 'components/common/header/Header'
import { Spacer } from 'wiz-front-shared/components/Spacer'
import { BottomSheet } from 'wiz-front-shared/components/bottomSheet/BottomSheet'
import { Button } from 'wiz-front-shared/components/button/Button'
import { ReactComponent as IcChevron } from 'assets/svgs/icon/ic_next_24.svg'
import { getServiceId } from 'common/service'
import './Terms.css'

interface TermsItem {
    termsId: number
    serviceId: string
    locale: string
    termsCode: string
    termsCodeName?: string
    termsTitle: string
    termsVersion: string
    isRequired: 'Y' | 'N'
}

interface TermsDetail {
    termsId: number
    termsTitle: string
    termsContent: string
    termsVersion?: string
}

/**
 * 약관 조회 페이지 — 회원이 가입 후 자신의 동의 약관을 조회.
 *
 * 진입 방식: DOKOCREW/DOKOPANG 등 호스트 앱이 iframe 으로 띄움 → URL 쿼리 `service=DOKOCREW|DOKOPANG` 로 분기.
 * service 결정 우선순위는 common/service.ts 의 getServiceId() 참조.
 * - 약관 목록: GET /wizcommon/api/v2/terms/active/{serviceId}?locale={locale}
 * - 본문: 항목 클릭 시 GET /wizcommon/api/v2/terms/{termsId} 로 lazy load → BottomSheet 표시
 */
export const Terms = () => {
    const { t, i18n } = useTranslation()
    const device = useAtomValue(deviceAtom)
    const inset = device.inset

    const termsListState = useAtomValue(termsStore.termsList.stateAtom)
    const termsDetailState = useAtomValue(termsStore.termsDetail.stateAtom)

    const [openTermsId, setOpenTermsId] = useState<number | null>(null)
    const [isSheetOpen, setIsSheetOpen] = useState(false)

    // BottomSheet 닫힘에 동기화 — scrim 클릭 등 외부에서 close 시 openTermsId 정리
    useEffect(() => {
        if (!isSheetOpen && openTermsId != null) {
            setOpenTermsId(null)
        }
    }, [isSheetOpen, openTermsId])

    const serviceId = useMemo(() => getServiceId(), [])

    useEffect(() => {
        termsStore.getTermsList(i18n.language || 'ko', serviceId)
    }, [i18n.language, serviceId])

    const termsList: TermsItem[] = useMemo(() => {
        const raw = termsListState.data?.data ?? termsListState.data ?? []
        return Array.isArray(raw) ? raw : []
    }, [termsListState.data])

    const handleOpen = useCallback((termsId: number) => {
        setOpenTermsId(termsId)
        setIsSheetOpen(true)
        termsStore.getTermsDetail(termsId)
    }, [])

    const handleClose = useCallback(() => {
        setIsSheetOpen(false)
    }, [])

    const detail: TermsDetail | null = useMemo(() => {
        if (openTermsId == null) return null
        if (!termsDetailState.isReady) return null
        const raw = termsDetailState.data?.data ?? termsDetailState.data
        if (!raw) return null
        return {
            termsId: Number(raw.termsId ?? openTermsId),
            termsTitle: raw.termsTitle ?? '',
            termsContent: raw.termsContent ?? '',
            termsVersion: raw.termsVersion ?? '',
        }
    }, [openTermsId, termsDetailState])

    const detailLoading = openTermsId != null && termsDetailState.isPending

    return (
        <div className="terms">
            <Header title={t('terms.title')} isBack />
            <div className="terms__content" style={{ paddingBottom: 16 + (inset?.navigationBarHeight ?? 0) }}>
                <Spacer height={12} />

                {termsListState.isPending && termsList.length === 0 && (
                    <div className="terms__empty">{t('common.loading')}</div>
                )}

                {!termsListState.isPending && termsList.length === 0 && (
                    <div className="terms__empty">{t('terms.empty')}</div>
                )}

                <div className="terms__list">
                    {termsList.map((term) => (
                        <button
                            key={term.termsId}
                            className="terms__item"
                            onClick={() => handleOpen(term.termsId)}
                        >
                            <span className="terms__item-text">
                                {term.isRequired === 'Y' && (
                                    <span className="terms__required">{t('common.required')}</span>
                                )}
                                <span className="terms__item-title">{term.termsTitle}</span>
                            </span>
                            <IcChevron className="terms__chevron" color="var(--darkGray)" />
                        </button>
                    ))}
                </div>
            </div>

            <BottomSheet isOpen={isSheetOpen} setIsOpen={setIsSheetOpen}>
                <div className="terms__sheet">
                    <div className="terms__sheet-title">
                        {detail?.termsTitle || ''}
                    </div>
                    <div className="terms__sheet-body">
                        {detailLoading ? (
                            <div className="terms__empty">{t('common.loading')}</div>
                        ) : (
                            <div
                                className="terms__sheet-content"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(detail?.termsContent || '') }}
                            />
                        )}
                    </div>
                    <div className="terms__sheet-footer">
                        <Button text={t('terms.btnClose')} onPress={handleClose} />
                    </div>
                </div>
            </BottomSheet>
        </div>
    )
}
